import { Code, ConnectError } from '@connectrpc/connect';
import { isAxiosError } from 'axios';

export type InterfacesReadRefusal = {
  kind: 'target_denied' | 'address_unresolved' | 'busy' | 'unavailable';
  retryAfterSeconds: number | undefined;
};

const kinds = {
  direct_snmp_target_denied: 'target_denied',
  interfaces_address_unresolved: 'address_unresolved',
  interfaces_admission_busy: 'busy',
  interfaces_admission_unavailable: 'unavailable',
} as const;

function boundedRetryAfter(value: unknown): number | undefined {
  if (typeof value !== 'string' || !/^[1-9]\d?$/.test(value)) return undefined;
  const seconds = Number(value);
  return seconds <= 90 ? seconds : undefined;
}

/** Only status/code pairs in the server contract are operator-facing policy refusals. */
export function interfacesReadRefusal(error: unknown): InterfacesReadRefusal | null {
  let status: number | Code | undefined;
  let code: unknown;
  let retryAfter: unknown;

  if (isAxiosError(error)) {
    status = error.response?.status;
    const data: unknown = error.response?.data;
    code = data !== null && typeof data === 'object' && !Array.isArray(data) && 'error' in data
      ? data.error
      : undefined;
    retryAfter = error.response?.headers?.['retry-after'] ?? error.response?.headers?.['Retry-After'];
  } else if (error instanceof ConnectError) {
    status = error.code;
    code = error.metadata.get('X-WorkerNet-Reason');
    retryAfter = error.metadata.get('Retry-After');
  } else {
    return null;
  }

  if (code === 'direct_snmp_target_denied' && (status === 422 || status === Code.FailedPrecondition)) {
    return { kind: kinds.direct_snmp_target_denied, retryAfterSeconds: undefined };
  }
  if (code === 'interfaces_address_unresolved' && (status === 422 || status === Code.FailedPrecondition)) {
    return { kind: kinds.interfaces_address_unresolved, retryAfterSeconds: undefined };
  }
  if (code === 'interfaces_admission_busy' && (status === 429 || status === Code.ResourceExhausted)) {
    return { kind: kinds.interfaces_admission_busy, retryAfterSeconds: boundedRetryAfter(retryAfter) };
  }
  if (code === 'interfaces_admission_unavailable' && (status === 503 || status === Code.Unavailable)) {
    return { kind: kinds.interfaces_admission_unavailable, retryAfterSeconds: undefined };
  }
  return null;
}

export function retryInterfacesQuery(failureCount: number, error: unknown): boolean {
  return interfacesReadRefusal(error) === null && failureCount < 3;
}

/** A refused query must remain dormant until an explicit operator action. */
export function interfacesQueryEnabled(requested: boolean, error: unknown): boolean {
  return requested && interfacesReadRefusal(error) === null;
}
